import { computed, ref } from 'vue';
import axios, {
  type AxiosAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

export interface PagingInfo {
  page: number;
  perPage: number;
  count: number;
}

export interface PagedResponse<T> {
  data: T;
  pagingInfo: PagingInfo;
  /** When the server clamped the requested `since` date (via X-Date-Range-Clamped
   *  response header) this is the max-lookback-days it applied. Use to toast
   *  the user that their date range was narrowed. */
  clampedToDays?: number;
}

export interface HttpClientOptions {
  baseURL: string;
  getToken: () => string | null;
  onUnauthorized: () => void;
  adapter?: AxiosAdapter;
}

export const inflightCount = ref(0);
export const isAjaxLoading = computed(() => inflightCount.value > 0);

export function createHttpClient(options: HttpClientOptions): AxiosInstance {
  const config: AxiosRequestConfig = { baseURL: options.baseURL };
  if (options.adapter) config.adapter = options.adapter;
  const instance = axios.create(config);

  instance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
    inflightCount.value += 1;
    const token = options.getToken();
    if (token) {
      req.headers.set('Authorization', `Bearer ${token}`);
    }
    return req;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      inflightCount.value = Math.max(0, inflightCount.value - 1);
      return response;
    },
    (error: unknown) => {
      inflightCount.value = Math.max(0, inflightCount.value - 1);
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        options.onUnauthorized();
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

let activeClient: AxiosInstance | null = null;

export function setHttpClient(client: AxiosInstance): void {
  activeClient = client;
}

export function getHttpClient(): AxiosInstance {
  if (!activeClient) {
    throw new Error(
      'HTTP client not initialized; call setHttpClient(createHttpClient(...)) in main.ts',
    );
  }
  return activeClient;
}

export function resetHttpClientForTests(): void {
  activeClient = null;
}

export async function http<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  const response = await getHttpClient().request<T>(config);
  return response.data;
}

export async function httpPaged<T = unknown>(
  config: AxiosRequestConfig,
): Promise<PagedResponse<T>> {
  const response = await getHttpClient().request<T>(config);
  const headerValue = response.headers?.['x-record-size'];
  const count = Number(headerValue ?? 0) || 0;
  const clampHeader = response.headers?.['x-date-range-clamped'];
  const clampedToDays = clampHeader !== undefined ? Number(clampHeader) || undefined : undefined;
  const params = (config.params ?? {}) as { page?: number; per?: number };
  return {
    ...(clampedToDays !== undefined ? { clampedToDays } : {}),
    data: response.data,
    pagingInfo: {
      page: Number(params.page ?? 1),
      perPage: Number(params.per ?? 0),
      count,
    },
  };
}
